import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { ReducedMotionToggle } from "@/components/ui/ReducedMotionToggle";
import oneFormIconDark from "@/assets/oneform-icon-dark.svg";
import oneFormIconLight from "@/assets/oneform-icon-light.svg";
import logoLight from "@/assets/oneform-logo-light.png";
import logoDark from "@/assets/oneform-logo-dark.png";

const navbarVariants = cva("z-[100] transition-all duration-500", {
  variants: {
    variant: {
      homepage: "fixed top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 rounded-xl sm:rounded-2xl",
      standard: "sticky top-0 bg-background border-b border-border",
    },
  },
  defaultVariants: {
    variant: "standard",
  },
});

interface PublicNavbarProps extends VariantProps<typeof navbarVariants> {
  className?: string;
}

export function PublicNavbar({ variant, className }: PublicNavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isHomepage = variant === "homepage";

  useEffect(() => {
    if (!isHomepage) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomepage]);

  const navContent = (
    <nav
      className={cn(
        navbarVariants({ variant }),
        className,
        "transition-all duration-300",
        isHomepage && isScrolled && "bg-slate-950/80 backdrop-blur-xl shadow-lg shadow-slate-900/50",
      )}
      id="navigation"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            {isHomepage ? (
              <>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <img src={oneFormIconLight} alt="OneForm" width={40} height={40} className="w-full h-full object-contain" />
                </div>
                <span className="text-lg sm:text-xl font-display font-medium text-white">OneForm</span>
              </>
            ) : (
              <>
                <img src={logoDark} alt="OneForm" width={120} height={32} className="h-8 dark:hidden" />
                <img src={logoLight} alt="OneForm" width={120} height={32} className="h-8 hidden dark:block" />
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <a
              href="#features"
              onClick={(e) => {
                if (isHomepage) {
                  e.preventDefault();
                  document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className={cn(
                "text-sm lg:text-base transition-all duration-200 font-medium cursor-pointer",
                isHomepage
                  ? "text-white/80 hover:text-white hover:scale-105"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Features
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                if (isHomepage) {
                  e.preventDefault();
                  document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className={cn(
                "text-sm lg:text-base transition-all duration-200 font-medium cursor-pointer",
                isHomepage
                  ? "text-white/80 hover:text-white hover:scale-105"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Pricing
            </a>
            <a
              href="#resources"
              className={cn(
                "text-sm lg:text-base transition-all duration-200 font-medium",
                isHomepage
                  ? "text-white/80 hover:text-white hover:scale-105"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Resources
            </a>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-6">
            {isHomepage && (
              <>
                <ThemeToggle />
                <ReducedMotionToggle showLabel={false} />
                <Link to="/guest">
                  <Button
                    variant="ghost"
                    className="font-medium rounded-lg lg:rounded-xl px-3 lg:px-6 text-sm lg:text-base text-white hover:text-white hover:bg-white/20"
                  >
                    Get Started Free
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    variant="premium"
                    className="font-medium shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300 hover:scale-105 rounded-lg lg:rounded-xl px-3 lg:px-6 text-sm lg:text-base"
                  >
                    Login
                  </Button>
                </Link>
              </>
            )}
            {!isHomepage && (
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/signup">Get Started</Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            {isHomepage && (
              <>
                <ThemeToggle />
                <ReducedMotionToggle showLabel={false} />
              </>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn(
                "p-2 transition-colors",
                isHomepage ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground",
              )}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden py-4 border-t border-border/50 mt-2">
            <div className="flex flex-col space-y-4">
              <a
                href="#features"
                onClick={(e) => {
                  if (isHomepage) {
                    e.preventDefault();
                    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                  }
                  setIsMobileMenuOpen(false);
                }}
                className={cn(
                  "text-sm font-medium transition-colors cursor-pointer",
                  isHomepage ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground",
                )}
              >
                Features
              </a>
              <a
                href="#pricing"
                onClick={(e) => {
                  if (isHomepage) {
                    e.preventDefault();
                    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    window.location.href = "/pricing";
                  }
                  setIsMobileMenuOpen(false);
                }}
                className={cn(
                  "text-sm font-medium transition-colors cursor-pointer",
                  isHomepage ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground",
                )}
              >
                Pricing
              </a>
              <a
                href="#resources"
                className={cn(
                  "text-sm font-medium transition-colors",
                  isHomepage ? "text-white/80 hover:text-white" : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Resources
              </a>
              {isHomepage && (
                <>
                  <Link to="/guest" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start font-medium">
                      Get Started Free
                    </Button>
                  </Link>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="premium" className="w-full justify-start font-medium shadow-lg shadow-primary/25">
                      Login
                    </Button>
                  </Link>
                </>
              )}
              {!isHomepage && (
                <Button asChild className="w-full justify-start">
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  return navContent;
}
